import React, { memo, useCallback, useState } from 'react'
import { Modal } from 'react-native'
import { useSelector, useDispatch } from 'APP/Hooks/redux'

import Analytics from 'APP/Services/Analytics'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { useChatContext, usePostContext } from 'APP/Lib/Context'
import Card from 'APP/Components/Chat/Card'
import TextMessage from 'APP/Components/Chat/TextMessage'
import AddressPickerSearchCard, {
  AddressPickerSearchCardProps,
} from 'APP/Converse/AddressPickerSearchCard'
import { getSelectedAddressPayload, transformAnswer } from 'APP/Lib/PlacesHelper'
import PatientActions, { selectPatientRegion } from 'APP/Redux/PatientRedux'
import { checkAddressRegionChange } from 'APP/Helpers/Intake'
import {
  Container,
  ModalContainer,
  WebModalOverlay,
  WebModalBackdrop,
  WebModalContent,
  StyledAddressPickerSearchCard,
} from './styles'
import { buildAddressFromResolvedPlace } from 'APP/Components/Intake/IntakeAddressSearch/helpers'
import { PlaceDetails } from '@dialogue/coredata'
import { AutocompleteOption } from 'APP/Converse/Inputs/AutocompleteInput'
import { getPayloadTemplateFromPost } from 'APP/Lib/MessagingHelpers'

const AddressPicker = () => {
  const { post } = usePostContext()
  const { setSelectedAddress } = useChatContext()
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)
  const [readyToOpen, setReadyToOpen] = useState(false)
  const currentRegion = useSelector(selectPatientRegion)

  const button = post?.props?.title ?? post?.props?.button ?? ''
  const place_type = post?.props?.place_type ?? ''
  const payload_template = getPayloadTemplateFromPost(post)

  const onPress = useCallback(() => {
    Analytics.trackEvent('button_click', { button_value: 'Address picker tap' })
    setReadyToOpen(false)
    setShowModal(true)
  }, [setShowModal])

  const closeModal = useCallback(() => {
    setShowModal(false)
    setReadyToOpen(false)
  }, [])

  const buildSelectedAddressPayload = useCallback(
    (
      resolvedPlace: PlaceDetails,
      placeType: string,
      payloadTemplate?: string,
      selectedOption?: AutocompleteOption | null
    ) => {
      const address = buildAddressFromResolvedPlace(resolvedPlace, selectedOption)
      const normalizedPlace = transformAnswer({
        ...resolvedPlace,
        placeId: resolvedPlace.external_id,
        address,
      })

      // Check if the region has changed and update the user's region
      const newRegion = checkAddressRegionChange(normalizedPlace, currentRegion)
      if (newRegion) {
        dispatch(PatientActions.patientProfileUpdateRegionRequest(newRegion))
        dispatch(PatientActions.scribeV2UserFetchTrigger())
      }

      return getSelectedAddressPayload(normalizedPlace, placeType, payloadTemplate)
    },
    [currentRegion, dispatch]
  )

  const onSubmitCallback = useCallback<
    NonNullable<AddressPickerSearchCardProps['onSubmitCallback']>
  >(
    (resolvedPlace, selectedOption) => {
      if (!resolvedPlace) return

      const selectedAddressPayload = buildSelectedAddressPayload(
        resolvedPlace,
        place_type,
        payload_template,
        selectedOption
      )

      const { payloadTemplate, payload, addressInfo } = selectedAddressPayload
      Analytics.trackEvent('button_click', { button_value: 'Address selected' })

      setSelectedAddress({ payloadTemplate, payload, addressInfo })

      closeModal()
    },
    [buildSelectedAddressPayload, setSelectedAddress, closeModal, place_type, payload_template]
  )

  const sharedCardProps = {
    placeholder: button,
    onSubmitCallback,
    type: place_type,
  }

  const iconName = place_type === 'establishment' ? 'pill' : 'map-marker'

  return (
    <Container>
      <TextMessage />
      <Card title={button} onPress={onPress} iconName={iconName} />
      {isWeb() ? (
        <Modal
          testID="WEB_MODAL"
          visible={showModal}
          animationType="fade"
          transparent
          statusBarTranslucent
          onRequestClose={() => setShowModal(false)}
        >
          <WebModalOverlay>
            <WebModalBackdrop onPress={closeModal} />
            <WebModalContent>
              <StyledAddressPickerSearchCard {...sharedCardProps} />
            </WebModalContent>
          </WebModalOverlay>
        </Modal>
      ) : (
        <>
          <Modal
            testID="MOBILE_MODAL"
            visible={showModal}
            animationType="slide"
            presentationStyle="pageSheet"
            onShow={() => setReadyToOpen(true)}
            onRequestClose={closeModal}
          >
            <ModalContainer>
              <AddressPickerSearchCard {...sharedCardProps} readyToOpen={readyToOpen} />
            </ModalContainer>
          </Modal>
        </>
      )}
    </Container>
  )
}

export default memo(AddressPicker)
